/**
 * Single source of truth für die Sidebar-Navigation.
 *
 * Jeder Eintrag deklariert die nötigen Permissions; die Sidebar rendert nur
 * was der Operator auch sehen darf. Permission-Strings sind
 * `me.permissions[]` aus `/v1/operator/me` (siehe operator-bootstrap.md).
 *
 * Routes hier MÜSSEN mit `routes.jsx` deckungsgleich sein.
 */

/**
 * @typedef {Object} NavItem
 * @property {string} label
 * @property {string} icon
 * @property {string} [to]          Sub-Items unterstützen via `children`
 * @property {string[]} [perms]
 * @property {string[]} [anyOf]
 * @property {NavItem[]} [children]
 */

/** @param {string} brandSlug @returns {NavItem[]} */
export function buildContextualNav(brandSlug) {
  return [
    {
      icon: 'pe-7s-graph2',
      label: 'Dashboard',
      to: `/${brandSlug}/dashboard`,
      perms: ['dashboard.read'],
    },
    {
      icon: 'pe-7s-mail',
      label: 'Tickets',
      to: `/${brandSlug}/tickets`,
      perms: ['tickets.read'],
    },
    {
      icon: 'pe-7s-note2',
      label: 'Runbooks / Wissen',
      to: `/${brandSlug}/ops-kb`,
      perms: ['kb.read'],
    },
    {
      icon: 'pe-7s-users',
      label: 'Benutzer',
      to: `/${brandSlug}/users`,
      perms: ['users.read'],
    },
    {
      icon: 'pe-7s-cart',
      label: 'Orders & Billing',
      anyOf: ['orders.read', 'subscriptions.read', 'cancelIntents.read'],
      children: [
        { label: 'Orders', to: `/${brandSlug}/orders`, perms: ['orders.read'] },
        { label: 'Subscriptions', to: `/${brandSlug}/subscriptions`, perms: ['subscriptions.read'] },
        { label: 'Cancel-Intents (§ 312q)', to: `/${brandSlug}/cancel-intents`, perms: ['cancelIntents.read'] },
      ],
    },
    {
      icon: 'pe-7s-box1',
      label: 'Catalog',
      anyOf: ['products.read', 'usage.read'],
      children: [
        { label: 'Products', to: `/${brandSlug}/products`, perms: ['products.read'] },
        { label: 'Abrechnungsläufe', to: `/${brandSlug}/usage-runs`, perms: ['products.read'] },
        { label: 'API-Marktplatz', to: `/${brandSlug}/api-catalog`, perms: ['products.read'] },
        { label: 'Usage', to: `/${brandSlug}/usage`, perms: ['usage.read'] },
      ],
    },
    {
      icon: 'pe-7s-key',
      label: 'API-Keys',
      to: `/${brandSlug}/api-keys`,
      perms: ['apiKeys.read'],
    },
    {
      icon: 'pe-7s-plug',
      label: 'Integrations',
      anyOf: ['drivers.read', 'integrations.read', 'stripeConnect.read', 'outboundWebhooks.read'],
      children: [
        { label: 'Driver-Status', to: `/${brandSlug}/drivers`, perms: ['drivers.read'] },
        { label: 'Provider-Credentials', to: `/${brandSlug}/integrations`, perms: ['integrations.read'] },
        { label: 'Stripe-Connect', to: `/${brandSlug}/stripe-connect`, perms: ['stripeConnect.read'] },
        { label: 'Outbound-Webhooks', to: `/${brandSlug}/outbound-webhooks`, perms: ['outboundWebhooks.read'] },
      ],
    },
    {
      icon: 'pe-7s-news-paper',
      label: 'Compliance',
      anyOf: ['audit.read', 'oss.read', 'policy.read'],
      children: [
        { label: 'Audit-Log', to: `/${brandSlug}/audit`, perms: ['audit.read'] },
        { label: 'OSS-Report (§ 18 UStG)', to: `/${brandSlug}/oss`, perms: ['oss.read'] },
        { label: 'AGB-Policy (§ 312j)', to: `/${brandSlug}/policy`, perms: ['policy.read'] },
      ],
    },
    {
      // Brand-Settings ist single-page (PUT /v1/admin/tenant-settings).
      // Packages/Pricing/SLA-Sub-Items wurden entfernt — Core hat dafür
      // keine eigenen Endpoints (siehe orbit-specs/done/admin-brand-settings-endpoints).
      icon: 'pe-7s-settings',
      label: 'Brand-Settings',
      to: `/${brandSlug}/brand-settings`,
      perms: ['tenantSettings.read'],
    },
  ];
}

/** @type {NavItem[]} */
export const ADMIN_NAV = [
  {
    icon: 'pe-7s-graph2',
    label: 'Übersicht',
    to: '/admin',
    perms: ['appSettings.read'],
  },
  {
    icon: 'pe-7s-server',
    label: 'Infrastructure',
    to: '/admin/infra',
    perms: ['appSettings.read'],
  },
  {
    icon: 'pe-7s-tools',
    label: 'Diagnostics',
    to: '/admin/diagnostics',
    perms: ['appSettings.read'],
  },
  {
    // Core-API Settings — Apps-Management entfernt (Phantom), Stripe/Lex
    // sind unter /admin/integrations, SLA ist Phantom.
    icon: 'pe-7s-portfolio',
    label: 'Core API',
    perms: ['appSettings.read'],
    children: [
      { label: 'Übersicht', to: '/admin/settings/core' },
      { label: 'App-Settings', to: '/admin/settings/app' },
      { label: 'Tax-Rates', to: '/admin/settings/core/tax-rates' },
      { label: 'Reseller-Verwaltung', to: '/admin/settings/core/resellers' },
      { label: 'Integrations', to: '/admin/integrations', perms: ['integrations.read'] },
    ],
  },
  {
    // Nexus-API — Plugin-/Provisioning-Layer. Per orbit-specs INBOX 8 Specs;
    // Plugin-Registry ist Spec 1, andere folgen iterativ.
    icon: 'pe-7s-config',
    label: 'Nexus API',
    perms: ['integrations.read'],
    children: [
      { label: 'Übersicht', to: '/admin/settings/nexus' },
      {
        label: 'Betrieb',
        children: [
          { label: 'Plugin-Registry', to: '/admin/settings/nexus/plugins' },
          { label: 'Tenant-Routing', to: '/admin/settings/nexus/routing' },
          { label: 'System-Settings', to: '/admin/settings/nexus/settings' },
          { label: 'Jobs-Monitor', to: '/admin/settings/nexus/jobs' },
          { label: 'Saga-Review', to: '/admin/settings/nexus/saga' },
          { label: 'Usage Explorer', to: '/admin/settings/nexus/usage' },
          { label: 'Customers', to: '/admin/settings/nexus/customers' },
          { label: 'Hosting-Operations', to: '/admin/settings/nexus/hosting-ops' },
        ],
      },
      {
        label: 'Infrastruktur',
        children: [
          { label: 'Pod-Cluster', to: '/admin/settings/nexus/pod-clusters' },
          { label: 'Server-Kapazität', to: '/admin/settings/nexus/server-capacity' },
          { label: 'Dedicated-Server', to: '/admin/settings/nexus/dedicated' },
          { label: 'INIS-Knoten', to: '/admin/settings/nexus/inis' },
          { label: 'VPS-Nodes', to: '/admin/settings/nexus/vps-nodes' },
          { label: 'Bulk-Storage', to: '/admin/settings/nexus/bulk-boxes' },
          { label: 'Webhosting (Enhance)', to: '/admin/settings/nexus/enhance' },
        ],
      },
      {
        label: 'Sicherheit & Mail',
        children: [
          { label: 'Mail-Security', to: '/admin/settings/nexus/mail-security' },
          { label: 'Postmaster SOC', to: '/admin/settings/nexus/postmaster' },
          { label: 'Mail-Review (Spam/Phishing)', to: '/admin/settings/nexus/mail-review' },
          { label: 'Ausgehende Absender (Velocity)', to: '/admin/settings/nexus/outbound-senders', perms: ['integrations.read'] },
          { label: 'DMARC-Berichte', to: '/admin/settings/nexus/dmarc' },
          { label: 'CrowdSec-Allowlist', to: '/admin/settings/nexus/gateway-allowlist' },
          { label: 'CrowdSec-Block-/Whitelisten', to: '/admin/settings/nexus/crowdsec-lists' },
        ],
      },
    ],
  },
  {
    // Produkt-Statistiken (cross-tenant, read-only) — /v1/admin/product-stats/*.
    // Bewusst eigene Gruppe statt unter „Nexus API": Kennzahlen, keine Betriebs-Tools.
    icon: 'pe-7s-graph1',
    label: 'Produkt-Statistiken',
    perms: ['integrations.read'],
    children: [
      { label: 'Base (BaaS)', to: '/admin/settings/nexus/base-stats' },
      { label: 'Backups', to: '/admin/settings/nexus/backup-stats' },
      { label: 'Netfabric', to: '/admin/settings/nexus/netfabric-stats' },
      { label: 'Managed-DBs', to: '/admin/settings/nexus/managed-db-stats' },
      { label: 'Deploy / Sites', to: '/admin/settings/nexus/deploy-stats' },
      { label: 'Lernplattform', to: '/admin/settings/nexus/learning-stats' },
      { label: 'Umzüge', to: '/admin/settings/nexus/migration-stats' },
      { label: 'Compliance-Monitor', to: '/admin/settings/nexus/compliance-stats' },
    ],
  },
  {
    // MTFF Theme + Plugin Control-Plane (vault: 02_applications/07_MTFF).
    // PR1 liefert Instance-Liste + Theme-Token-Editor + Preview-Token.
    icon: 'pe-7s-paint-bucket',
    label: 'MTFF',
    perms: ['integrations.read'],
    children: [
      { label: 'Instances', to: '/admin/settings/mtff/instances' },
      { label: 'Webhook-Log', to: '/admin/settings/mtff/webhooks' },
    ],
  },
  {
    icon: 'pe-7s-network',
    label: 'Probe API',
    perms: ['integrations.read'],
    children: [
      { label: 'Übersicht', to: '/admin/settings/probe' },
      { label: 'Scraping', to: '/admin/settings/probe/scrape' },
      { label: 'WireGuard', to: '/admin/settings/probe/wireguard' },
      { label: 'KEDA', to: '/admin/settings/probe/keda' },
    ],
  },
  {
    icon: 'pe-7s-graph1',
    label: 'Pulse API',
    perms: ['integrations.read'],
    children: [
      { label: 'Übersicht', to: '/admin/settings/pulse' },
      { label: 'Wissensdatenbank (errorDB/LogDB)', to: '/admin/settings/pulse/knowledge' },
      { label: 'Security-Dashboard', to: '/admin/settings/pulse/security' },
      { label: 'CrowdSec-Statistiken', to: '/admin/settings/pulse/crowdsec' },
      { label: 'Zabbix', to: '/admin/settings/pulse/zabbix' },
      { label: 'Telemetry', to: '/admin/settings/pulse/telemetry' },
      { label: 'cpGuard', to: '/admin/settings/pulse/cpguard' },
    ],
  },
  {
    // #261 ValiSight — KI-Log-Analyse. Operator-Kuration der Erklärungen.
    icon: 'pe-7s-note2',
    label: 'ValiSight',
    perms: ['integrations.read'],
    children: [
      { label: 'Log-Kuration', to: '/admin/settings/valisight/curation' },
      { label: 'Alert-Regeln', to: '/admin/settings/valisight/alert-rules' },
    ],
  },
  {
    // Public APIs (valitype.io) — Scraping-Produkte auf Basis von Probe.
    // Aktuell: Events API. Weitere öffentliche APIs kommen als Kinder hinzu.
    icon: 'pe-7s-global',
    label: 'Public APIs',
    perms: ['integrations.read'],
    children: [
      {
        label: 'Events',
        children: [
          { label: 'Übersicht', to: '/admin/apis/events' },
          { label: 'Quellen & Schedule', to: '/admin/apis/events/sources' },
          { label: 'Seite testen', to: '/admin/apis/events/test' },
          { label: 'Event-Katalog', to: '/admin/apis/events/catalog' },
        ],
      },
      {
        label: 'E‑Rechnung',
        children: [
          { label: 'Übersicht', to: '/admin/apis/ebill' },
          { label: 'Erzeugen', to: '/admin/apis/ebill/generate' },
          { label: 'Validieren', to: '/admin/apis/ebill/validate' },
          { label: 'Artefakte', to: '/admin/apis/ebill/artifacts' },
          { label: 'Statistik', to: '/admin/apis/ebill/stats' },
          { label: 'Einstellungen', to: '/admin/apis/ebill/settings' },
        ],
      },
      {
        label: 'Preise',
        children: [
          { label: 'Übersicht', to: '/admin/apis/price' },
          { label: 'Quellen & Schedule', to: '/admin/apis/price/sources' },
          { label: 'Seite testen', to: '/admin/apis/price/test' },
          { label: 'Produkt-Katalog', to: '/admin/apis/price/catalog' },
          { label: 'Läden', to: '/admin/apis/price/stores' },
          { label: 'Statistik', to: '/admin/apis/price/stats' },
        ],
      },
      {
        label: 'WP API',
        children: [
          { label: 'Übersicht', to: '/admin/apis/wp' },
          { label: 'Plugin-Store', to: '/admin/apis/wp/plugins' },
          { label: 'Rechtstexte', to: '/admin/apis/wp/templates' },
          { label: 'Instanzen', to: '/admin/apis/wp/instances' },
          { label: 'Telemetrie', to: '/admin/apis/wp/telemetry' },
          { label: 'Modul-Tarife', to: '/admin/apis/wp/modules' },
          { label: 'Angriffsabwehr', to: '/admin/apis/wp/security' },
          { label: 'Rechts-Compliance', to: '/admin/apis/wp/compliance' },
          { label: 'Schwachstellen', to: '/admin/apis/wp/vuln' },
        ],
      },
      {
        label: 'Weitere APIs',
        children: [
          { label: 'Subservices (LibreTranslate/Nominatim/Ollama)', to: '/admin/apis/subservices' },
          { label: 'Geocoding API', to: '/admin/apis/geocode' },
          { label: 'Translate API', to: '/admin/apis/translate' },
          { label: 'LLM API', to: '/admin/apis/llm' },
          { label: 'Trademark API', to: '/admin/apis/trademark' },
          { label: 'Platform API', to: '/admin/apis/platform' },
          { label: 'Spam-/Phishing-API', to: '/admin/apis/spam' },
          { label: 'Scan-API', to: '/admin/apis/scan' },
          { label: 'Barrierefreiheit-API', to: '/admin/apis/a11y' },
          { label: 'Such-API', to: '/admin/apis/search' },
        ],
      },
    ],
  },
];
